import { useState, useEffect } from 'react';
import { SignedIn, SignedOut, useAuth } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useMeRole } from '../hooks/useMeRole';

/**
 * Customer account: purchase history, receipts, favorites (save for later).
 * Shown only to customers (no CityId). Role from DB.
 */
export default function Account() {
  const { getToken, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const { isVendor, loading: roleLoading } = useMeRole();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailClaimMissing, setEmailClaimMissing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Only fetch if signed in (component is wrapped in SignedIn, but double-check)
      if (!isSignedIn) {
        if (!cancelled) setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      setEmailClaimMissing(false);
      try {
        const res = await api('/api/sales/buyer', { getToken });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          // User-friendly error messages
          if (res.status === 401) {
            if (!cancelled) {
              setPurchases([]);
              setError('Please sign in to view your purchase history');
              setLoading(false);
            }
            return;
          }
          throw new Error(data.error || `Unable to load purchase history. Please try again later.`);
        }
        const list = Array.isArray(data) ? data : (data.purchases ?? []);
        if (!cancelled) {
          setPurchases(list);
          setEmailClaimMissing(data.emailUnavailable === true);
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken, isSignedIn]);

  return (
    <SignedIn>
      <div className="flex flex-col gap-6">
        <h1 className="text-3xl font-semibold">Account</h1>
        <p className="text-lg text-slate-600 dark:text-slate-400">
          Your purchase history and account details.
        </p>
        {error && (
          <div className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4">
            {error}
          </div>
        )}
        {emailClaimMissing && (
          <div className="rounded-xl border-2 border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-900/20 p-4">
            <p className="font-medium text-amber-900 dark:text-amber-100">Purchase history uses your account email.</p>
            <p className="text-sm text-slate-700 dark:text-slate-300 mt-1">Add the <strong>email</strong> claim to your Clerk JWT template (Clerk Dashboard → JWT Templates → claim name <code>email</code>, value <code>{'{{user.primary_email_address}}'}</code>), then sign out and back in.</p>
          </div>
        )}
        {loading ? (
          <div className="text-slate-500 dark:text-slate-400 py-8">Loading…</div>
        ) : (
          <>
            <section>
              <h2 className="text-xl font-semibold mb-3">Purchase history</h2>
              {purchases.length === 0 ? (
                <p className="text-slate-600 dark:text-slate-400">No purchases yet.</p>
              ) : (
                <ul className="flex flex-col gap-3">
                  {purchases.map((p) => (
                    <li
                      key={p.id}
                      className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 flex justify-between items-center"
                    >
                      <div>
                        <span className="font-medium">{p.itemTitle}</span>
                        <span className="text-slate-600 dark:text-slate-400 block text-sm">
                          ${p.amount?.toFixed(2)} · {p.vendorName ?? 'Vendor'}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate('/my-purchases')}
                        className="text-indigo-600 dark:text-indigo-400 font-medium"
                      >
                        View all
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
            <button
              type="button"
              onClick={() => navigate('/my-purchases')}
              className="h-14 w-full rounded-xl font-semibold bg-slate-200 dark:bg-slate-700 text-slate-800 dark:text-slate-200"
            >
              View all purchases
            </button>
            
            {/* Start a Garage Sale button - only show to customers (not garage sellers) */}
            {!roleLoading && !isVendor && (
              <div className="border-t border-slate-200 dark:border-slate-700 pt-6 mt-6">
                <h2 className="text-xl font-semibold mb-3">Want to sell items?</h2>
                <p className="text-slate-600 dark:text-slate-400 mb-4">
                  Start your own garage sale and list items for sale.
                </p>
                <SignedIn>
                  <button
                    type="button"
                    onClick={() => navigate('/vendor/login')}
                    className="h-14 w-full rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                  >
                    Start a Garage Sale
                  </button>
                </SignedIn>
                <SignedOut>
                  <button
                    type="button"
                    onClick={() => navigate('/vendor/login')}
                    className="h-14 w-full rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                  >
                    Sign In to Start a Garage Sale
                  </button>
                </SignedOut>
              </div>
            )}
          </>
        )}
      </div>
    </SignedIn>
  );
}
